import { Alert, Stack, Typography } from "@mui/material";
import { useCreateIdentityFromUserMutation } from "Auth/Api/Mutations/Migration/useCreateIdentityFromUserMutation";
import { OnboardingLayout } from "Components/Layout/OnboardingLayout";
import { LinkToCustomerForm } from "Components/Migration/LinkToCustomerForm";
import { MerchantCreateIdentity } from "Components/Migration/MerchantCreateIdentity/MerchantCreateIdentity";
import { useMigrationSessionHandoff } from "Components/Migration/useMigrationSessionHandoff";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { PartyType, UserStatus, Version } from "Infrastructure/Api/Api";
import { AccessRoleCodes, isBackOfficeRole } from "Models/AccessRoleCodes";
import { Navigate } from "Navigation/Navigate";
import { useNavigate } from "Navigation/useNavigate";
import { resetUser } from "State/Auth/AuthReducer";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import {
  getSessionStorageItem,
  removeSessionStorageItem,
} from "Utils/Storage/SessionStorage/SecureSessionStorage";
import { SessionStorageKeys } from "Utils/Storage/SessionStorage/SessionStorageKeys";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

const GOOGLE_CODE_PARAM = "googleCode";
const GOOGLE_NONCE_PARAM = "nonce";

type GoogleReturn = {
  code: string | null;
  isRejected: boolean;
};

/**
 * Reads the Google link round trip out of the URL. The code counts only with the nonce this tab
 * stored before redirecting; otherwise a crafted URL could link the caller to a foreign account.
 */
const readGoogleReturn = (search: string): GoogleReturn => {
  const params = new URLSearchParams(search);
  const code = params.get(GOOGLE_CODE_PARAM);
  if (!code) {
    return { code: null, isRejected: false };
  }

  const expectedNonce = getSessionStorageItem(
    SessionStorageKeys.GoogleLinkNonce,
  );
  const isNonceValid =
    !!expectedNonce && params.get(GOOGLE_NONCE_PARAM) === expectedNonce;

  return isNonceValid
    ? { code, isRejected: false }
    : { code: null, isRejected: true };
};

const COMPONENT_RESOURCES = Resources.Auth.Migration;

type MigrationStep = "choice" | "link-to-customer" | "merchant-create-identity";

type BlockedReason = {
  isEmployee: boolean;
  isBackOffice: boolean;
};

const getBlockedReasonResource = ({
  isEmployee,
  isBackOffice,
}: BlockedReason) => {
  if (isBackOffice) {
    return COMPONENT_RESOURCES.BackOfficeNotSupported;
  }

  if (isEmployee) {
    return COMPONENT_RESOURCES.EmployeeNotSupported;
  }

  return COMPONENT_RESOURCES.AccountNotReady;
};

/**
 * Blocking migration prompt shown to non-Customer Users who haven't linked to an Identity yet.
 * User picks one of two flows:
 *  1. Link to an existing Customer Identity (enter credentials of their existing Customer account)
 *  2. Create a new Identity from a person: NaturalPerson/OSVČ users one-click reuse their own data;
 *     LegalEntity users pick which statutory representative they are (legal requirement). Both land
 *     in the Customer context and are routed straight into the Customer onboarding.
 */
export const MigrationRequiredPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // The Google link round trip lands back here with the code in the URL: open the link step and
  // let the form consume it (or show why it was refused). Snapshot once on mount.
  const [googleReturn] = useState(() => readGoogleReturn(location.search));
  const hasGoogleReturn = !!googleReturn.code || googleReturn.isRejected;
  const [step, setStep] = useState<MigrationStep>(
    hasGoogleReturn ? "link-to-customer" : "choice",
  );
  const [serverError, setServerError] = useState<string | null>(null);
  const currentUser = useLoggedInUser();
  const { t } = useResource();
  const dispatch = useDispatch();
  const handoffSession = useMigrationSessionHandoff();

  // One-shot cleanup: the nonce is spent and the code must not survive a refresh in the URL.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only
  useEffect(() => {
    if (!hasGoogleReturn) {
      return;
    }
    removeSessionStorageItem(SessionStorageKeys.GoogleLinkNonce);
    navigate(appUrl("migration-required"), { replace: true });
  }, []);

  const {
    mutate: createIdentity,
    isPending: isCreatingIdentity,
    isSuccess: isIdentityCreated,
  } = useCreateIdentityFromUserMutation({
    onSuccess: handoffSession,
    onError: () => {
      setServerError(t(COMPONENT_RESOURCES.CreateError));
    },
  });

  if (!currentUser) {
    return <Navigate to={appUrl("sign-in")} replace />;
  }

  // If user is no longer flagged for migration, bounce away. A freshly migrated session lands
  // in the Customer context as MooneLite — funnel it straight into the Customer onboarding.
  // Anything else (e.g. linked to an already-FullMoone Customer) belongs on the dashboard.
  if (!currentUser.migrationRequired) {
    const isLiteCustomerContext =
      currentUser.accessRoleCode === AccessRoleCodes.Customer &&
      currentUser.version === Version.MooneLite;

    return (
      <Navigate
        to={appUrl(
          isLiteCustomerContext ? "complete-customer-onboarding" : "dashboard",
        )}
        replace
      />
    );
  }

  // Who may link/create here: completed accounts only. Accounts mid-registration (Onboarding,
  // RoleSelection, ...) are blocked on purpose and recreated instead of being pushed through the
  // legacy wizard. Employees are blocked as well: their legacy User is anchored on the person, while
  // the Identity grant belongs to the company, so linking would produce the wrong shape — the
  // merchant re-invites them instead. Back-office accounts (Admin, Aml, ...) are provisioned by an
  // administrator on their own Identity and never self-migrate — the backend rejects both flows.
  const isMigratableStatus =
    currentUser.userStatus === UserStatus.Active ||
    currentUser.userStatus === UserStatus.EmailConfirmed;
  const isEmployee = currentUser.accessRoleCode === AccessRoleCodes.Employee;
  const isBackOffice = isBackOfficeRole(currentUser.accessRoleCode);

  if (!isMigratableStatus || isEmployee || isBackOffice) {
    return (
      <OnboardingLayout title={t(COMPONENT_RESOURCES.Title)}>
        <Stack gap={3}>
          <Alert severity="warning">
            {t(getBlockedReasonResource({ isEmployee, isBackOffice }))}
          </Alert>
          <PrimaryButton
            variant="outlined"
            color="secondary"
            onClick={() => dispatch(resetUser({}))}
          >
            {t(COMPONENT_RESOURCES.SignOut)}
          </PrimaryButton>
        </Stack>
      </OnboardingLayout>
    );
  }

  // The flow is driven by the User.Party TYPE, not the role:
  //  - NaturalPerson / SelfEmployed (OSVČ) are person-backed → one-click create (the backend reuses
  //    the Party or materializes a NaturalPerson Identity.Party from it).
  //  - LegalEntity (company) has no personal data → open the person-picker step to collect/pick one.
  const userRole = currentUser.accessRoleCode;
  const partyType = currentUser.partyType;
  const needsPersonPicker = partyType === PartyType.LegalEntity;
  const canCreateNewIdentity = partyType != null;

  const handleCreateIdentity = () => {
    setServerError(null);
    if (needsPersonPicker) {
      setStep("merchant-create-identity");
      return;
    }
    createIdentity({});
  };

  return (
    <OnboardingLayout title={t(COMPONENT_RESOURCES.Title)}>
      {step === "choice" && (
        <Stack gap={3}>
          <Typography variant="h6">{t(COMPONENT_RESOURCES.Welcome)}</Typography>
          <Typography variant="body1">
            {t(COMPONENT_RESOURCES.Description)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(COMPONENT_RESOURCES.RoleHint, {
              role: userRole ?? "",
            })}
          </Typography>
          {serverError && <Alert severity="error">{serverError}</Alert>}
          <Stack gap={2} mt={2}>
            <PrimaryButton
              variant="outlined"
              color="secondary"
              onClick={() => setStep("link-to-customer")}
              disabled={isCreatingIdentity || isIdentityCreated}
            >
              {t(COMPONENT_RESOURCES.LinkToCustomerOption)}
            </PrimaryButton>
            <PrimaryButton
              variant="outlined"
              color="secondary"
              onClick={handleCreateIdentity}
              disabled={
                !canCreateNewIdentity || isCreatingIdentity || isIdentityCreated
              }
            >
              {canCreateNewIdentity
                ? t(COMPONENT_RESOURCES.CreateIdentityOption)
                : t(COMPONENT_RESOURCES.CreateIdentityDeferred)}
            </PrimaryButton>
          </Stack>
        </Stack>
      )}

      {step === "link-to-customer" && (
        <LinkToCustomerForm
          onBack={() => setStep("choice")}
          googleCode={googleReturn.code}
          hasRejectedGoogleReturn={googleReturn.isRejected}
        />
      )}

      {step === "merchant-create-identity" && (
        <MerchantCreateIdentity onBack={() => setStep("choice")} />
      )}
    </OnboardingLayout>
  );
};
